import React from "react";
import { Heart, Star } from "lucide-react";
import useStore from "../../store/useStore";
import { isSizeInStock } from "../../utils/inventoryUtils";
import { getAvailableSizes } from "../../utils/productUtils";

const ProductCard = ({ product, onViewDetails }) => {
  const addToCart = useStore((state) => state.addToCart);
  const toggleWishlist = useStore((state) => state.toggleWishlist);
  const isInWishlist = useStore((state) => state.isInWishlist(product.id));
  const setCartOpen = useStore((state) => state.setCartOpen);
  const cart = useStore((state) => state.cart);

  const availableSizes = getAvailableSizes(product);
  const sizeSoldOut =
    availableSizes.length > 0 &&
    !availableSizes.some((size) => isSizeInStock(product, size, cart));
  const isOutOfStock = product.outOfStock || sizeSoldOut;

  // Every cart line must carry a size: the server rejects sizeless orders.
  // One size -> pick it automatically. Several -> send them to the detail
  // page to choose, rather than adding something we'd reject at checkout.
  const inStockSizes = availableSizes.filter((size) =>
    isSizeInStock(product, size, cart)
  );

  const handleAddToCart = (e) => {
    e.stopPropagation();
    e.preventDefault(); // Extra safety
    if (isOutOfStock) return;

    if (inStockSizes.length === 1) {
      addToCart(product, inStockSizes[0]);
      setCartOpen(true);
      return;
    }
    onViewDetails(product);
  };

  const handleToggleWishlist = (e) => {
    e.stopPropagation();
    e.preventDefault(); // Extra safety
    toggleWishlist(product);
  };

  const handleViewDetails = (e) => {
    e.stopPropagation();
    e.preventDefault(); // Extra safety
    onViewDetails(product);
  };

  const handleCardClick = () => {
    onViewDetails(product);
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-2xl transition-all duration-300 group border border-gray-100 cursor-pointer"
    >
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
        <img
          src={product.thumbs?.[0] || product.images[0]}
          alt={product.name}
          className="w-full aspect-square object-contain group-hover:scale-110 transition duration-500"
        />

        {/* Wishlist Button - FIXED with container stopPropagation */}
        <div
          className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10"
          onClick={handleToggleWishlist}
        >
          <button
            className={`p-2 sm:p-3 rounded-full shadow-lg transition ${
              isInWishlist
                ? "bg-red-500 text-white hover:bg-red-600"
                : "bg-white hover:bg-[#e8e4f3]"
            }`}
          >
            <Heart
              className={`w-4 h-4 sm:w-5 sm:h-5 ${
                isInWishlist ? "fill-current" : "text-gray-600"
              }`}
            />
          </button>
        </div>
        {isOutOfStock && (
          <div className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-gray-900/80 text-white px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-semibold">
            Out of Stock
          </div>
        )}

        {/* View Details Overlay Button */}
        <div
          onClick={handleViewDetails}
          className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition hidden sm:flex items-end justify-center pb-6"
        >
          <span className="bg-white text-gray-900 px-6 py-3 rounded-full font-semibold shadow-xl transform translate-y-4 group-hover:translate-y-0 transition">
            View Details
          </span>
        </div>
      </div>

      <div className="p-3 sm:p-6">
        <div className="flex items-center justify-between mb-1 sm:mb-2">
          <span className="text-[10px] sm:text-xs font-semibold text-[#d4af37] uppercase tracking-wider">
            {product.gender}
          </span>
          <div className="hidden sm:flex items-center space-x-1">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-3.5 h-3.5 ${
                  i < product.rating
                    ? "text-[#d4af37] fill-current"
                    : "text-gray-300"
                }`}
              />
            ))}
          </div>
        </div>

        <h4 className="text-sm sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3 line-clamp-2 group-hover:text-[#d4af37] transition">
          {product.name}
        </h4>

        <div className="hidden sm:block">
          <p className="text-sm text-gray-500 mb-4 line-clamp-2">
            {product.description}
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pt-2 sm:pt-4 border-t border-gray-100">
          <span className="text-base sm:text-2xl font-bold text-gray-900">
            ${product.price.toLocaleString()}
          </span>

          {/* Add to Cart Button */}
          <button
            onClick={handleAddToCart}
            disabled={isOutOfStock}
            className={`w-full sm:w-auto px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold transition shadow-lg ${
              isOutOfStock
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-gradient-to-r from-[#1a1a3e] to-[#2a2a5e] text-white hover:from-[#2a2a5e] hover:to-[#1a1a3e] hover:shadow-xl"
            }`}
          >
            {isOutOfStock
              ? "Out of Stock"
              : inStockSizes.length === 1
              ? "Add to Cart"
              : "Choose Size"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
